import React from "react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";

/**
 * Initials-based avatar — coloured circle with the person's initials.
 * No external HTTP call. Pure CSS + deterministic Maisonéa palette.
 *
 * Props:
 *   name      Full display name, e.g. "Léa Martin" — preferred source for initials.
 *   email     Fallback if name is missing — e.g. "lea.martin@maisonea.fr".
 *   seed      Optional override for deterministic colour selection.
 *   size      "xs" (24) | "sm" (32) | "md" (40) | "lg" (64). Default "sm".
 *   className Extra Tailwind classes for the wrapper.
 *   actions   Optional array of {label, icon?, onClick, disabled?, destructive?}
 *             — when provided the avatar becomes clickable and opens a
 *             contextual dropdown menu.
 *   menuLabel Optional menu header label (shown above the actions).
 */
const SIZE = {
  xs: { box: "w-6 h-6", text: "text-[10px]" },
  sm: { box: "w-8 h-8", text: "text-[11px]" },
  md: { box: "w-10 h-10", text: "text-[13px]" },
  lg: { box: "w-16 h-16", text: "text-[22px]" },
};

const PALETTE = [
  { bg: "bg-terracotta", fg: "text-cream" },
  { bg: "bg-sage", fg: "text-cream" },
  { bg: "bg-warm-dark", fg: "text-cream" },
  { bg: "bg-terracotta-dark", fg: "text-cream" },
  { bg: "bg-sage/80", fg: "text-cream" },
];

function initialsFrom(name, email) {
  const source = (name || email || "?").trim();
  if (!source) return "?";
  const local = source.includes("@") ? source.split("@")[0] : source;
  const parts = local.split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return source[0].toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function colorIndex(seed) {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) {
    h = (h * 31 + seed.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % PALETTE.length;
}

export default function Avatar({ name, email, seed, size = "sm", className = "", actions, menuLabel }) {
  const sizeCfg = SIZE[size] || SIZE.sm;
  const initials = initialsFrom(name, email);
  const key = (seed || name || email || "u").toLowerCase();
  const palette = PALETTE[colorIndex(key)];
  const baseClass = `inline-flex items-center justify-center rounded-full border border-sand select-none font-heading font-semibold ${sizeCfg.box} ${sizeCfg.text} ${palette.bg} ${palette.fg} ${className}`;

  if (!actions || actions.length === 0) {
    return (
      <span
        data-testid="avatar"
        className={baseClass}
        title={name || email || ""}
        aria-label={name || email || "avatar"}
      >
        {initials}
      </span>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="avatar-button"
          className={`${baseClass} cursor-pointer transition-transform hover:scale-110 hover:ring-2 hover:ring-terracotta/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta`}
          title={name || email || ""}
          aria-label={`Actions pour ${name || email || "utilisateur"}`}
        >
          {initials}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56 bg-off-white border-sand" data-testid="avatar-menu">
        {menuLabel && (
          <>
            <DropdownMenuLabel className="text-warm-dark/70 text-[11px] font-semibold uppercase tracking-wider">
              {menuLabel}
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-sand" />
          </>
        )}
        {actions.map((a, idx) => (
          <DropdownMenuItem
            key={`${a.label}-${idx}`}
            onClick={(e) => { e.preventDefault(); a.onClick?.(); }}
            disabled={a.disabled}
            className={`text-[13px] cursor-pointer ${a.destructive ? "text-error focus:text-error" : "text-warm-dark"}`}
            data-testid={`avatar-action-${a.testKey || a.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {a.icon && <span className="mr-2 inline-flex">{a.icon}</span>}
            {a.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
